import {
  X,
  User,
  Phone,
  MapPin,
  CreditCard,
  Package,
  Calendar,
  Receipt,
  Truck,
  Tag,
  MessageCircle,
  ArrowLeft,
  Percent,
  IndianRupee,
  Utensils,
  Navigation,
  StickyNote,
  Gift,
  CheckCircle,
  Star,
  CircleDot,
  Hash,
  Clock,
  Banknote,
  Building2,
} from "lucide-react";

const money = (v) => Number(v || 0).toFixed(2);

const parseJSON = (value) => {
  try {
    if (!value) return null;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return null;
  }
};

const formatText = (value, fallback = "N/A") => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null"
  ) {
    return fallback;
  }
  return value;
};

const StatusBadge = ({ children }) => (
  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold capitalize">
    <CircleDot size={10} />
    {children}
  </span>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mt-5 mb-3">
    <Icon size={14} className="text-gray-400" />
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
      {children}
    </span>
  </div>
);

const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  const items = Array.isArray(order?.items) ? order.items : [];
  const addressJson = parseJSON(order?.address_json);
  const addressValues = addressJson?.values || {};

  const subtotal = money(order?.subtotal);
  const sgst = money(order?.tax_sgst);
  const cgst = money(order?.tax_cgst);
  const totalGST = money(
    Number(order?.tax_sgst || 0) + Number(order?.tax_cgst || 0)
  );
  const deliveryFee = money(order?.delivery_fee);
  const packagingCharges = money(order?.packaging_charges);
  const orderCharges = money(order?.order_charges);
  const discountAmount = money(order?.discount_amount);
  const couponDiscount = money(order?.coupon_code_discount);
  const loyaltyDiscount = money(order?.loyalty_points_discount);
  const messageCharges = money(order?.message_charges);
  const messageGst = money(order?.message_charges_tax_gst);
  const payAmount = money(order?.pay_amount);

  const initials = (order?.customer_name || "NA")
    .split(" ")
    .map((word) => word?.[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 montserrat-font-1">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white border-b px-5 py-4 flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
          >
            <ArrowLeft size={18} />
          </button>

          <div>
            <h2 className="font-bold text-gray-900">
              Order #{formatText(order?.orderid)}
            </h2>
            <p className="text-xs text-gray-500">
              {formatText(order?.order_date)}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <StatusBadge>{formatText(order?.status)}</StatusBadge>

            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-72px)] px-5 pb-6">
          {/* Customer Summary */}
          <div className="mt-4 rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
              {initials}
            </div>

            <div className="flex-1">
              <h3 className="font-bold text-gray-900">
                {formatText(order?.customer_name)}
              </h3>
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Phone size={12} />
                {formatText(order?.customer_mobile)}
              </p>
              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Building2 size={12} />
                {formatText(order?.us_name)}
              </p>
            </div>

            <div className="text-right">
              <p className="text-xs text-gray-500">Pay Amount</p>
              <p className="text-2xl font-bold text-green-700">₹{payAmount}</p>
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="rounded-xl bg-red-50 border border-red-100 p-3">
              <p className="text-[11px] uppercase text-red-500 font-semibold">
                Payment Status
              </p>
              <p className="mt-1 text-sm font-bold text-red-700 flex items-center gap-1 capitalize">
                <Banknote size={13} />
                {formatText(order?.payment_status)}
              </p>
            </div>

            <div className="rounded-xl bg-blue-50 border border-blue-100 p-3">
              <p className="text-[11px] uppercase text-blue-500 font-semibold">
                Payment Mode
              </p>
              <p className="mt-1 text-sm font-bold text-blue-700 flex items-center gap-1">
                <CreditCard size={13} />
                {formatText(order?.payment_mode)}
              </p>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
              <p className="text-[11px] uppercase text-amber-500 font-semibold">
                Delivery Status
              </p>
              <p className="mt-1 text-sm font-bold text-amber-700 flex items-center gap-1 capitalize">
                <Truck size={13} />
                {formatText(order?.delivery_partner_status)}
              </p>
            </div>

            <div className="rounded-xl bg-purple-50 border border-purple-100 p-3">
              <p className="text-[11px] uppercase text-purple-500 font-semibold">
                Platform
              </p>
              <p className="mt-1 text-sm font-bold text-purple-700 flex items-center gap-1 capitalize">
                <MessageCircle size={13} />
                {formatText(order?.platform)}
              </p>
            </div>
          </div>

          {/* Quick Info */}
          <SectionTitle icon={Receipt}>Order Info</SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InfoBox icon={Calendar} label="Order Date" value={order?.order_date} />
            <InfoBox icon={Clock} label="Updated At" value={order?.update} />
            <InfoBox icon={Package} label="Order Type" value={order?.orderType} />
            <InfoBox icon={Hash} label="Daily Order ID" value={order?.daily_order_id} />
            <InfoBox icon={CreditCard} label="Virtual Account" value={order?.virtual_acc} />
            <InfoBox icon={Tag} label="Refund Status" value={order?.refund_status} />
          </div>

          {/* Items */}
          <SectionTitle icon={Utensils}>Items ({items.length})</SectionTitle>

          <div className="space-y-3">
            {items.length > 0 ? (
              items.map((item, index) => {
                const customization = parseJSON(item?.customization);

                return (
                  <div
                    key={`${item?.id}-${index}`}
                    className="rounded-2xl bg-gray-50 border border-gray-100 p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                        <Utensils size={17} />
                      </div>

                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {formatText(item?.item_name || item?.product_id, "Item")}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          Qty: {formatText(item?.quantity, "0")} × ₹
                          {money(item?.item_price)}
                        </p>

                        {customization && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {Object.entries(customization).map(([key, value]) => (
                              <span
                                key={key}
                                className="text-[11px] px-2 py-1 rounded-full bg-white border text-gray-600"
                              >
                                {key}:{" "}
                                <span className="font-semibold text-gray-800">
                                  {value}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <p className="font-bold text-gray-900">
                        ₹{money(item?.total_price)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-400">
                No items found
              </div>
            )}
          </div>

          {/* Price Breakdown */}
          <SectionTitle icon={IndianRupee}>Price Breakdown</SectionTitle>

          <div className="rounded-2xl bg-green-50 border border-green-100 p-4 space-y-3">
            <AmountRow label="Subtotal" value={subtotal} />
            <AmountRow label="SGST" value={sgst} />
            <AmountRow label="CGST" value={cgst} />
            <AmountRow label="Total GST" value={totalGST} />
            <AmountRow label="Delivery Fee" value={deliveryFee} />
            <AmountRow label="Packaging Charges" value={packagingCharges} />
            <AmountRow label="Order Charges" value={orderCharges} />

            {Number(discountAmount) > 0 && (
              <AmountRow label="Discount" value={`- ${discountAmount}`} red />
            )}

            {Number(couponDiscount) > 0 && (
              <AmountRow
                label={`Coupon Discount ${
                  order?.coupon_code ? `(${order.coupon_code})` : ""
                }`}
                value={`- ${couponDiscount}`}
                red
              />
            )}

            {Number(loyaltyDiscount) > 0 && (
              <AmountRow label="Loyalty Discount" value={`- ${loyaltyDiscount}`} red />
            )}

            <div className="border-t pt-3 flex justify-between items-center">
              <span className="font-bold text-gray-900">Total Payable</span>
              <span className="text-xl font-bold text-green-700">₹{payAmount}</span>
            </div>
          </div>

          {/* Message Charges */}
          <SectionTitle icon={MessageCircle}>Message Charges</SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InfoBox
              icon={MessageCircle}
              label="Message Count"
              value={`${formatText(order?.message_count, "0")} Message(s)`}
            />
            <InfoBox
              icon={IndianRupee}
              label="Per Message Cost"
              value={`₹${money(order?.per_message_cost)}`}
            />
            <InfoBox
              icon={Receipt}
              label="Message Charges + GST"
              value={`₹${messageCharges} + ₹${messageGst}`}
            />
          </div>

          {/* Address */}
          <SectionTitle icon={MapPin}>Delivery Address</SectionTitle>

          <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex gap-2">
              <Navigation size={15} className="text-gray-400 mt-1" />
              <p className="text-sm text-gray-700 leading-6">
                {formatText(order?.address_line, "No address provided")}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <MiniInfo label="Tower" value={addressValues?.tower_number} />
              <MiniInfo label="Floor" value={addressValues?.floor_number} />
              <MiniInfo label="Landmark" value={addressValues?.landmark_area} />
              <MiniInfo label="Pincode" value={addressValues?.in_pin_code} />
              <MiniInfo label="City" value={addressValues?.city} />
              <MiniInfo label="State" value={addressValues?.state} />
              <MiniInfo label="Latitude" value={order?.latitude} />
              <MiniInfo label="Longitude" value={order?.longitude} />
            </div>
          </div>

          {/* Additional Info */}
          <SectionTitle icon={User}>Additional Details</SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InfoBox icon={Truck} label="Delivery Person" value={order?.delivery_person_name || "Not assigned"} />
            <InfoBox icon={Phone} label="Delivery Number" value={order?.delivery_person_number || "Not assigned"} />
            <InfoBox icon={StickyNote} label="Kitchen Note" value={order?.kitchen_note || "None"} />
            <InfoBox icon={MessageCircle} label="Remark" value={order?.remark || "None"} />
            <InfoBox icon={Gift} label="Loyalty Points" value={order?.loyalty_points || "0"} />
            <InfoBox icon={CheckCircle} label="GST Claim" value={order?.gst_claim === "Y" ? "Yes" : "No"} />
            <InfoBox icon={Star} label="Coupon Code" value={order?.coupon_code || "Not applied"} />
            <InfoBox icon={Hash} label="Table No." value={order?.table_no || "N/A"} />
            <InfoBox icon={Clock} label="Time Slot" value={order?.time || "Not set"} />
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoBox = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
    <p className="text-[11px] uppercase text-gray-500 font-semibold mb-1">
      {label}
    </p>
    <p className="text-sm text-gray-900 font-medium flex items-start gap-2 break-words">
      <Icon size={13} className="text-gray-400 mt-0.5 shrink-0" />
      {formatText(value)}
    </p>
  </div>
);

const MiniInfo = ({ label, value }) => (
  <div className="rounded-xl bg-white border border-gray-100 p-3">
    <p className="text-[11px] uppercase text-gray-400 font-semibold">
      {label}
    </p>
    <p className="text-sm text-gray-800 font-medium mt-1">
      {formatText(value, "—")}
    </p>
  </div>
);

const AmountRow = ({ label, value, red = false }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-600">{label}</span>
    <span className={`font-semibold ${red ? "text-red-500" : "text-gray-900"}`}>
      ₹{value}
    </span>
  </div>
);

export default OrderDetailsModal;